import {
  LOCKABLE_TEAM_FIELD_LABELS,
  LOCKABLE_USER_FIELD_LABELS,
  type LockableTeamField,
  type LockableUserField,
  type Preference,
  type StrictSubareaPreference,
} from '@terros/common'
import { type SelectItem } from '../types'

export const PREFERENCE_GROUPS = ['Canvass', 'Calendar', 'Reports', 'Recorder', 'Users & Teams'] as const
export type PreferenceGroup = (typeof PREFERENCE_GROUPS)[number]

type Scope = 'user' | 'teams' | 'company'

type PreferenceDefinition = {
  group: PreferenceGroup
  scopes: Scope[]
  deprecated?: boolean
  label: string
  description: string
}

export const PREFERENCE_DEFINITIONS = {
  // #region Canvass
  secondsBetweenPitches: {
    label: 'Time Between Pitches',
    description: 'Time that must pass between pitches. Set to 0 for no limit.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  metersBetweenPitchesToday: {
    label: 'Distance Between Pitches',
    description: 'The distance a user must move between pitches. Set to 0 for no limit.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  maxMetersFromLocationToActivity: {
    label: 'Max distance to log activity',
    description:
      'Maximum distance from a property that a rep can be to update their last activity. Set to 0 for no limit.',
    group: 'Canvass',
    scopes: ['company'],
  },
  maxMetersFromLocationToPitch: {
    label: 'Pitch Distance from Property',
    description: 'The user must be within this distance of a property to log a pitch. Set to 0 for no limit.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  maxMetersFromEventToDisposition: {
    label: 'Disposition Distance from Appointment',
    description:
      'The user must be within this distance of the appointment location to update its outcome. Set to 0 for no limit.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  daysBetweenPitch: {
    label: 'Time Between Re-Pitching',
    description:
      'The amount of time that must pass before a user can re-pitch a property. Set to -1 to disable users from pitching the same property multiple times.',
    group: 'Canvass',
    scopes: ['company'],
  },
  requireClockInBeforeContact: {
    label: 'Require Clock-In Before Contact',
    description: 'When enabled, users must clock in before contacting a property.',
    group: 'Canvass',
    scopes: ['company'],
  },
  phonePitch: {
    label: 'Phone Pitching',
    description: 'When on, the user will be asked whether a pitch was a phone or door pitch.',
    group: 'Canvass',
    scopes: ['company'],
  },
  dispositionMode: {
    label: 'Disposition Mode',
    description: 'Determines how dispositions should be created.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  showCommercialProperties: {
    label: 'Show Commercial Properties',
    description: 'Shows more property types on the map, including commercial properties, warehouses, etc.',
    group: 'Canvass',
    scopes: ['company'],
  },
  showMultiUnitDwellings: {
    label: 'Show Multi-Unit Dwellings',
    description:
      'Shows more property types on the map, including multi-unit dwellings, residential complexes, apartment buildings, etc.',
    group: 'Canvass',
    scopes: ['company'],
  },
  hideInactivePins: {
    label: 'Hide Inactive Pins',
    description: 'When on, pins with no sales activity are not shown.',
    group: 'Canvass',
    scopes: ['company'],
  },
  hiddenCanvassMapLayers: {
    label: 'Hidden Canvass Map Layers',
    description: 'Map layers selected here will be hidden in Canvass.',
    group: 'Canvass',
    scopes: ['company'],
  },
  hiddenCanvassUpsells: {
    label: 'Hidden Canvass Upsells',
    description: 'Upsell prompts selected here will be hidden in Canvass.',
    group: 'Canvass',
    scopes: ['company'],
  },
  country: {
    label: 'Country',
    description: 'The country your company operates in. This affects location data, geocoding, and location matching.',
    group: 'Canvass',
    scopes: ['company'],
  },
  blockCustomLocationDetailEdits: {
    label: 'Block Custom Location Detail Edits',
    description:
      'When on, users cannot edit a custom location address. Canvass matches the address and creates the location automatically.',
    group: 'Canvass',
    scopes: ['company'],
  },
  areaVisibility: {
    label: 'Area Visibility',
    description: 'Determines which areas are visible to users.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  limitByArea: {
    label: 'Limit Canvass Area',
    description: 'When on, prevents users from seeing any properties outside their assigned area.',
    group: 'Canvass',
    scopes: ['company'],
  },
  enableAreaManagement: {
    label: 'Enable Area Management',
    description: 'When enabled, users can manage areas in the system.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  strictSubareaAssignment: {
    label: 'Strict Subarea Assignment',
    description:
      'Controls whether managers must draw areas within their team territory boundaries. Off: no checks. Warn: shows warnings. Block: prevents saving outside boundaries.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  enableAccountManagement: {
    label: 'Enable Account Management',
    description: 'When enabled, users can manage accounts in the system.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  allowDuplicateAccounts: {
    label: 'Allow Duplicate Accounts',
    description: 'Controls how duplicate accounts are handled when creating new accounts.',
    group: 'Canvass',
    scopes: ['company'],
  },
  requireLeadResidentInfo: {
    label: 'Require Lead Resident Info',
    description: 'When enabled, users must provide resident information when creating a lead.',
    group: 'Canvass',
    scopes: ['company'],
    deprecated: true,
  },
  enerflo: {
    label: 'Enerflo Integration',
    description: 'Enable Enerflo integration for your company.',
    group: 'Canvass',
    scopes: ['company'],
  },
  taskLockout: {
    label: 'Task Lockout',
    description: 'When on, users must complete required tasks before accessing Canvass or Stats.',
    group: 'Canvass',
    scopes: ['company'],
  },
  planLockout: {
    label: 'Plan Lockout',
    description: 'When on, users must submit their quarterly plan before accessing Canvass or Stats.',
    group: 'Canvass',
    scopes: ['teams', 'company'],
  },
  // #endregion

  // #region Calendar
  closerInteraction: {
    label: 'Closer Interaction',
    description:
      'Full shows both setter and closer stats, with close events enabled. Partial shows one set of stats, but close events are still enabled. None shows one set of stats with close events disabled.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  closerStatus: {
    label: 'Default Closer Status',
    description: 'The status that will be assumed for all users that do not have their closer status set.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  closerModel: {
    label: 'Assigning Closers',
    description: 'Determines how closers are assigned to appointments.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  systemCloserStrategy: {
    label: 'System Assignment Strategy',
    description:
      'When closer assignment is set to system, this determines how closers are selected. Random picks any available closer. Balanced distributes by event count. Preferential picks by closer rank. Performance picks by a KPI metric.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  closerSettings: {
    label: 'Closer Settings',
    description: 'Configure closer timeslots, availability, and scheduling rules.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  restrictSetterCloseEvents: {
    label: 'Restrict Overbooking',
    description: 'When on, setters can only book events when there are available closers.',
    group: 'Calendar',
    scopes: ['company'],
  },
  bookingPercent: {
    label: 'Appointments per Timeslot',
    description: 'The number of appointments that a closer can booked for a given timeslot',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  maxMinutesSetterCanDeleteEvent: {
    label: 'Delete Events',
    description:
      'The amount of time that a setter has to delete an appoinment after it is created. Set to 0 minutes to prevent setters from deleting events. Set to -1 to allow setters to delete events at any time.',
    group: 'Calendar',
    scopes: ['company'],
  },
  meetingDuration: {
    label: 'Default Appointment Duration',
    description: 'Default length of appointments.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  timeBetweenAppointments: {
    label: 'Default Time Between Appointments',
    description: 'The default time between appointments.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  daysBetweenInterview: {
    label: 'Days Between One-on-Ones',
    description: 'The number of days that must pass before a manager is prompted to schedule another one-on-one.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  daysBetweenShadow: {
    label: 'Days Between Shadowing',
    description: 'The number of days that must pass before a rep is prompted to schedule another shadow.',
    group: 'Calendar',
    scopes: ['teams', 'company'],
  },
  updateAvailabilityTask: {
    label: 'Update Availability Task Frequency',
    description:
      'Set to -1 to disable. Closers are prompted when their availability has not been updated in this many days.',
    group: 'Calendar',
    scopes: ['company'],
  },
  // #endregion

  // #region Reports
  leaderboardColumns: {
    label: 'Leaderboard Metrics',
    description:
      'Options (up to 10) to allow users to select on the leaderboard stats. If none are selected, Terros will default to the most popular metrics.',
    group: 'Reports',
    scopes: ['teams', 'company'],
  },
  defaultStatsColumns: {
    label: 'Default Stats Metrics',
    description: 'Select the default metrics that users will see when they first access the stats page.',
    group: 'Reports',
    scopes: ['company'],
  },
  // #endregion

  // #region Recorder
  recordingReminder: {
    label: 'Recording Reminder',
    description: 'How often to remind users to start recording when canvassing.',
    group: 'Recorder',
    scopes: ['company'],
  },
  conversationExpiration: {
    label: 'Expire Conversations After',
    description: 'How long to keep recorded conversations for before deleting. Set to 0 to never delete.',
    group: 'Recorder',
    scopes: ['company'],
  },
  conversationLocationUpdateThreshold: {
    label: 'Conversation Location Update Maximum Distance',
    description: 'The new location must be within this distance when updating a Skill Insights conversation location',
    group: 'Recorder',
    scopes: ['teams', 'company'],
  },
  requireEvalToCanvass: {
    label: 'Require Recorded Pitch Before Canvassing',
    description: 'Reps must record at least one pitch before they can open Canvass.',
    group: 'Recorder',
    scopes: ['company'],
  },
  autoAssignEvals: {
    label: 'Auto-Assign Skill Evaluations',
    description: 'When on, skill evaluations will be automatically assigned to a manager.',
    group: 'Recorder',
    scopes: ['company'],
  },
  // #endregion

  // #region Users & Teams
  enableVisibilityRestrictions: {
    label: 'Enable Team Restrictions',
    description: 'When on, admins can configure restricted visibility by team.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  canBeInMultipleTeams: {
    label: 'Allow Multiple Team Memberships',
    description: 'When on, Terros will not auto remove previous teams that a user is on.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  managerBasedTeams: {
    label: 'Enable Manager-Based Teams',
    description: 'When enabled, users will see a simplified version of team management built around managers.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  daysBetweenAskManager: {
    label: 'Days Between Manager Prompt',
    description: 'How often to ask users to confirm who they report to. Set to -1 to disable.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  inactiveDaysBeforeArchive: {
    label: 'Automatically archive users after x days of inactivity',
    description:
      'The number of days of inactivity before a user is automatically archived. Set to 0 to disable automatic archiving.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  lockedUserFields: {
    label: 'Locked User Fields',
    description:
      'User profile fields that only people who can manage users can change. Everyone else sees them as read-only.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  lockedTeamFields: {
    label: 'Locked Team Fields',
    description: 'Team fields that only people who can manage users can change. Everyone else sees them as read-only.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  sso: {
    label: 'Single Sign-On (SSO)',
    description:
      'Enable enterprise login and user sync. This disables password reset, email changes, and some team management features.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  multiLanguage: {
    label: 'Multi-Language Support',
    description: 'Allow company to operate in multiple languages.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  unitSystem: {
    label: 'Units',
    description: 'The units to use for measurements.',
    group: 'Users & Teams',
    scopes: ['company'],
  },
  pinnedTabs: {
    label: 'Pinned Tabs',
    description: 'The tabs to pin in the navigation bar on the iOS app.',
    group: 'Users & Teams',
    scopes: ['company', 'teams'],
  },
  favoritedFeatures: {
    label: 'Favorited Features',
    description: 'Features favorited by this user.',
    group: 'Users & Teams',
    scopes: ['user'],
  },
  accountListViewOrder: {
    label: 'Accounts View Order',
    description: 'Personal ordering of saved views on the Accounts page.',
    group: 'Users & Teams',
    scopes: ['user'],
  },
  areaManagementViewOrder: {
    label: 'Area Management View Order',
    description: 'Personal ordering of saved views on the Area Management page.',
    group: 'Users & Teams',
    scopes: ['user'],
  },
  customerMap: {
    label: 'Customer Stages',
    description: 'The stages shown by the customer layer on the iOS app.',
    group: 'Canvass',
    scopes: ['company'],
  },
  // #endregion
} satisfies Record<Preference, PreferenceDefinition>

export const PREFERENCE_DEFINITIONS_BY_GROUP = Object.entries(PREFERENCE_DEFINITIONS).reduce(
  (acc, [key, definition]) => {
    const group = definition.group
    if (!acc[group]) {
      acc[group] = []
    }
    acc[group].push({ preference: key as Preference, ...definition })
    return acc
  },
  {} as Record<PreferenceGroup, Array<PreferenceDefinition & { preference: Preference }>>
)

export type PreferenceWithDefinition = PreferenceDefinition & { preference: Preference }

export const STRICT_SUBAREA_ITEMS: SelectItem<StrictSubareaPreference>[] = [
  { value: 'off', label: 'Allow creating areas outside team territory' },
  { value: 'warn', label: 'Warn when creating areas outside team territory' },
  { value: 'block', label: 'Block creating areas outside team territory' },
]

export const LOCKABLE_USER_FIELD_ITEMS: SelectItem<LockableUserField>[] = [...LOCKABLE_USER_FIELD_LABELS].map(
  ([value, label]) => ({ value, label })
)

export const LOCKABLE_TEAM_FIELD_ITEMS: SelectItem<LockableTeamField>[] = [...LOCKABLE_TEAM_FIELD_LABELS].map(
  ([value, label]) => ({ value, label })
)
